import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { DatePipe } from '@angular/common';
import { TariffRateService } from '../../service/tariff-rate.service';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { deepCopy, getDataScopeByPermission, getForwarderByPermission, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { finalize } from 'rxjs/operators';
import * as xlsx from 'xlsx';
import { UserType } from 'src/app/core/enums/user-type.enum';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-tariff-rate-setting',
  templateUrl: './tariff-rate-setting.component.html',
  styleUrls: ['./tariff-rate-setting.component.scss']
})
export class TariffRateSettingComponent extends SessionBaseComponent implements OnInit {
  readonly preferenceKey: string;
  readonly permissionCode = PermissionConstants.ViewTariffRateSetting;
  currentLang: string = this.laasSetting.language;
  forwarderOptions: any;
  isActivatedOptions: any;
  columns: any[];
  isSearchFormOpen: boolean;
  searchParams: any;
  searchForm: FormGroup;
  tariffRateRows: any[];
  isLoading: boolean;
  isShowImportTariffRateModal: boolean;

  constructor(
    private dropdownOptionsService: DropdownOptionsService,
    private tariffRateService: TariffRateService,
    injector: Injector,
    private fb: FormBuilder,
    private datePipe: DatePipe
  ) {
    super(injector);
    this.preferenceKey = UserPreferenceConstants.TariffRateSetting;
    this.isSearchFormOpen = true;
    this.isLoading = false;
    this.isShowImportTariffRateModal = false;
    this.tariffRateRows = [];

    this.columns = [
        { field: 'forwarder', header: 'TariffRate.MainTable.Forwarder', sort: true },
        { field: 'sku', header: 'TariffRate.MainTable.SKU', sort: true },
        { field: 'tariffRate', header: 'TariffRate.MainTable.TariffRate', sort: true },
        { field: 'customsTariff', header: 'TariffRate.MainTable.CustomsNumber', sort: true },
        { field: 'skuDescription', header: 'TariffRate.MainTable.PartNumDescript', sort: true },
        { field: 'declarationDt', header: 'TariffRate.MainTable.FilingDate', sort: true },
        { field: 'brand', header: 'TariffRate.MainTable.Brand', sort: true },
        { field: 'isActivated', header: 'TariffRate.MainTable.IsValid', sort: true },
        { field: 'startDt', header: 'TariffRate.MainTable.StartDate', sort: true },
        { field: 'endDt', header: 'TariffRate.MainTable.EndDate', sort: true },
        { field: 'createUser', header: 'TariffRate.MainTable.Creator', sort: true },
        { field: 'createDt', header: 'TariffRate.MainTable.CreationTime', dataType: 'date', sort: true },
        { field: 'updateUser', header: 'TariffRate.MainTable.UpdateBy', sort: true },
        { field: 'updateDt', header: 'TariffRate.MainTable.UpdateTime', dataType: 'date', sort: true },
        { field: '__action', header: 'TariffRate.MainTable.Action', sort: false },
    ];

    this.searchParams =  {
      sku: null,
      forwarder: [],
      isActivated: '1'
    };

    this.searchForm = this.fb.group({
      sku: null,
      forwarder: [],
      isActivated: '1'
    });

    this.isActivatedOptions = [];
  }

  async ngOnInit(): Promise<void> {
    this.logger.info('TariffRateSettingComponent onInit.');
    await super.ngOnInit();

    this.isActivatedOptions = [
      { label: 'Y', value: '1' },
      { label: 'N', value: '0' }
    ];

    this.dropdownOptionsService.getForwarderOptions().subscribe({
      next: (options: any[]) => {
        this.forwarderOptions = options.filter((x: any) => {
          return ( this.userProfile.userType === UserType.SuperAdmin.toString() ||
          this.userProfile.userType === UserType.Shipping.toString() ||
          getForwarderByPermission(this.userProfile, this.permissionCode).includes(x.optionCode));
        }).map(x => ({ label: x.optionDesc, value: x.optionCode }));
      }
    });

    //rbac to maintain this page
    var canInPage = this.permissionCodes.includes(this.permissionCode);
    if (!canInPage) {
      //go unAuthorize
      this.routeStateService.navigateTo('/Error/unAuthorize');
    }
  }


  searchClickBtn(event: any): void{
    console.log('params: ' + JSON.stringify(this.searchParams));
    this.isLoading = true;
    this.searchParams.sku = isNullOrEmpty(this.searchForm.value.sku) ? null : this.searchForm.value.sku.trim();
    const params = deepCopy(this.searchParams);
    params.dataScopes = getDataScopeByPermission(this.userProfile, this.permissionCode);

    this.tariffRateService.getTariffRate(params)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.isSearchFormOpen = false;
      }))
      .subscribe({
        next: result => {
          this.tariffRateRows = result;
        }
      });
  }

  resetClickBtn(event: any): void{
    this.searchForm.reset();
  }

  addClickBtn(event: any): void{
    this.routeStateService.navigateTo('/Setting/TariffRateSettingAdd', true);
  }

  importClickBtn(event: any): void{
    this.isShowImportTariffRateModal = true;
  }

  exportClickBtn(event: any): void{
    const dataRows = [];
    for (const item of this.tariffRateRows) {
      const row = {} as any;
      for (const column of this.columns) {
        if (item.hasOwnProperty(column.field)) {
          let value = item[column.field];
          switch (column.field){
            case 'forwarder':
              value = this.forwarderOptions.find((x: any) => x.value === value)?.label;
              break;
            case 'isActivated':
              value = this.isActivatedOptions.find((x: any) => x.value === value)?.label;
              break;
            case 'declarationDt':
              value = this.datePipe.transform(value, 'yyyy-MM-dd', this.laasSetting.facilityTimeZone);
              break;
            case 'startDt':
              value = this.datePipe.transform(value, 'yyyy-MM-dd', this.laasSetting.facilityTimeZone);
              break;
            case 'endDt':
              value = this.datePipe.transform(value, 'yyyy-MM-dd', this.laasSetting.facilityTimeZone);
              break;
            default:
              switch (column.dataType) {
                case 'date':
                  value = this.datePipe.transform(value, 'yyyy-MM-dd HH:mm:ss', this.laasSetting.facilityTimeZone);
                  break;
                default:
                  break;
              }
              break;
          }
          row[column.header] = value;
        }
      }
      dataRows.push(row);
    }

    const worksheet = xlsx.utils.json_to_sheet(dataRows);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data: Blob = new Blob(
      [excelBuffer],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }
    );
    const url = URL.createObjectURL(data);
    const linkEle = document.createElement('a');
    linkEle.href = url;
    linkEle.download = 'Tariff Rate Result';
    linkEle.click();
    URL.revokeObjectURL(url);
    linkEle.remove();
  }

  actionBtnClick(event: any, rowData: any): void{
    this.routeStateService.navigateTo('/Setting/TariffRateSettingDetail', rowData, true);
  }

  importTariffRate(event: any): void{
    this.isShowImportTariffRateModal = false;
  }
}
